import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";

/**
 * A short ordered list of the site's (brands, figures, testimonials): add up to
 * the schema's maximum, remove, move up and down. The order is the page's.
 */
export function SiteListEditor<T>({
  items,
  max,
  itemLabel,
  addLabel,
  newItem,
  onChange,
  renderItem,
}: {
  items: T[];
  max: number;
  /** Names row `i` for screen readers: "Marca 2". */
  itemLabel: (index: number) => string;
  addLabel: string;
  newItem: () => T;
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number, set: (item: T) => void) => React.ReactNode;
}): React.JSX.Element {
  function set(index: number, item: T): void {
    onChange(items.map((current, i) => (i === index ? item : current)));
  }
  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    if (moved === undefined) return;
    next.splice(target, 0, moved);
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        // Index keys: rows have no id of their own, and the order IS the identity here.
        <div key={i} className="rounded-md border border-ink-100 bg-ink-050 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-ink-500">{itemLabel(i)}</span>
            <div className="flex items-center gap-1 text-ink-500">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label={`Subir ${itemLabel(i)}`}
                className="rounded p-1 hover:bg-white disabled:opacity-30"
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                aria-label={`Bajar ${itemLabel(i)}`}
                className="rounded p-1 hover:bg-white disabled:opacity-30"
              >
                <ArrowDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                aria-label={`Quitar ${itemLabel(i)}`}
                className="rounded p-1 hover:bg-white"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-3">{renderItem(item, i, (next) => set(i, next))}</div>
        </div>
      ))}
      {items.length < max ? (
        <button
          type="button"
          onClick={() => onChange([...items, newItem()])}
          className="self-start text-[13px] font-medium text-moss hover:underline"
        >
          + {addLabel}
        </button>
      ) : (
        <span className="text-[12px] text-ink-500">Máximo {max}.</span>
      )}
    </div>
  );
}
